import * as React from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import useStyles from "./Radio.styles";
import { useMouseHover } from "../../../hooks/useMouseHover";
import { FontAwesomeIcon } from "../../shared/FontAwesome";

interface ButtonProps<T> {
    value?: T;
    text?: string;
    layout?: "vertical" | "horizontal";
    icon?: () => any | IconProp;
}

interface ButtonProtectedProps<T> extends ButtonProps<T> {
    active?: boolean;
    onClick?(): void;
}

function RadioButton<T>({ text, icon, layout, ...otherProps }: ButtonProps<T>) {
    const { active, onClick } = otherProps as ButtonProtectedProps<T>;
    const [hover, pointerHandlers] = useMouseHover();
    const classes = useStyles({ active, hover, layout });

    return (
        <div className={classes.button} {...pointerHandlers} onClick={onClick} {...otherProps}>
            {icon && (
                <div className={classes.buttonIcon}>
                    {typeof icon === "function" ? icon() : <FontAwesomeIcon icon={icon} />}
                </div>
            )}
            {text}
        </div>
    );
}

interface Props<T> {
    value: T;
    children: React.ReactElement<ButtonProtectedProps<T>>[];
    onChange(value: T): void;
}

export default function Radio<T>({ value, onChange, children, ...otherProps }: Props<T>) {
    const classes = useStyles({});

    return (
        <div className={classes.radio} {...otherProps}>
            {React.Children.map(children, (c) =>
                React.cloneElement(c, {
                    active: c.props.value === value,
                    onClick: () => onChange(c.props.value),
                }),
            )}
        </div>
    );
}

Radio.Button = RadioButton;
